import React, { useState, useEffect, useCallback } from 'react';
import {
  getIndices, getNifty100,
  formatPrice, formatVolume, changeColor, changeSign,
  isMarketOpen, getMarketSession,
} from '../../services/indiaAPI';
import { THEME } from '../../styles/theme';

const S = {
  wrap:    { padding: '20px', maxWidth: '1200px', margin: '0 auto' },
  header:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title:   { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px' },
  badge:   (color) => ({ padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', letterSpacing: '1px', background: color + '22', color, border: `1px solid ${color}44` }),
  refreshBtn: { padding: '6px 14px', borderRadius: '6px', border: `1px solid rgba(0,212,255,0.3)`, background: 'transparent', color: THEME.colors.blue, cursor: 'pointer', fontSize: '12px', fontFamily: THEME.fonts.hud },

  section:  { marginBottom: '28px' },
  secLabel: { fontSize: '10px', fontWeight: '700', letterSpacing: '3px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px', fontFamily: THEME.fonts.hud },

  indexGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '12px' },
  indexCard: (color, isUp) => ({
    background:   'rgba(255,255,255,0.03)',
    border:       `1px solid ${color}33`,
    borderRadius: '10px',
    padding:      '14px 16px',
    cursor:       'default',
  }),
  idxName:  { fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '6px' },
  idxPrice: { fontSize: '20px', fontWeight: '800', color: '#fff', fontFamily: THEME.fonts.hud, letterSpacing: '1px' },
  idxChg:   (pct) => ({ fontSize: '13px', fontWeight: '700', color: changeColor(pct), marginTop: '4px' }),

  twoCol:   { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  movers:   { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '16px' },
  mTitle:   (color) => ({ fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color, marginBottom: '12px' }),

  row:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' },
  rowName:  { fontSize: '13px', fontWeight: '600', color: '#e2e8f0' },
  rowSub:   { fontSize: '10px', color: 'rgba(255,255,255,0.3)', marginTop: '1px' },
  rowRight: { textAlign: 'right' },
  rowPrice: { fontSize: '13px', fontWeight: '700', color: '#fff' },
  rowChg:   (pct) => ({ fontSize: '12px', fontWeight: '700', color: changeColor(pct) }),

  sectorGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '10px' },
  secCard:  (pct) => ({
    background:   changeColor(pct) + '11',
    border:       `1px solid ${changeColor(pct)}22`,
    borderRadius: '8px',
    padding:      '12px 14px',
    display:      'flex',
    justifyContent: 'space-between',
    alignItems:   'center',
  }),
  secName:  { fontSize: '12px', color: '#e2e8f0', fontWeight: '600' },
  secPct:   (pct) => ({ fontSize: '13px', fontWeight: '800', color: changeColor(pct) }),

  loading:  { textAlign: 'center', padding: '60px', color: THEME.colors.textMuted, fontSize: '14px' },
  dot:      { display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', marginRight: '6px', animation: 'pulse 2s infinite' },
  ts:       { fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginLeft: '12px' },
};

export default function IndiaOverview() {
  const [indices,   setIndices]   = useState([]);
  const [nifty100,  setNifty100]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [n100Loading, setN100Loading] = useState(true);
  const [lastUpdate, setLastUpdate]  = useState(null);
  const session = getMarketSession();

  const loadIndices = useCallback(async () => {
    try {
      const data = await getIndices();
      setIndices(data);
      setLastUpdate(new Date());
    } catch {}
    setLoading(false);
  }, []);

  const loadNifty100 = useCallback(async () => {
    try {
      const data = await getNifty100();
      setNifty100(data.filter(s => !s.error));
    } catch {}
    setN100Loading(false);
  }, []);

  useEffect(() => {
    loadIndices();
    loadNifty100();
    const t = setInterval(loadIndices, 60_000);
    return () => clearInterval(t);
  }, [loadIndices, loadNifty100]);

  // Separate broad indices from sector indices
  const broadIdx  = indices.filter(i => i.group === 'BROAD');
  const sectorIdx = indices.filter(i => i.group === 'SECTOR');

  // Top 5 gainers and losers from Nifty 100
  const sorted   = [...nifty100].filter(s => s.changePct != null).sort((a, b) => b.changePct - a.changePct);
  const gainers  = sorted.slice(0, 5);
  const losers   = sorted.slice(-5).reverse();

  // Sector performance from nifty100 data
  const sectorMap = {};
  nifty100.forEach(s => {
    if (!s.sector || s.changePct == null) return;
    if (!sectorMap[s.sector]) sectorMap[s.sector] = { sum: 0, count: 0 };
    sectorMap[s.sector].sum   += s.changePct;
    sectorMap[s.sector].count += 1;
  });
  const sectors = Object.entries(sectorMap)
    .map(([name, v]) => ({ name, avg: v.sum / v.count }))
    .sort((a, b) => b.avg - a.avg)
    .slice(0, 12);

  const ts = lastUpdate
    ? lastUpdate.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;

  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>INDIA MARKET</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>
            Nifty 50 + Nifty Next 50 · NSE/BSE Live
            {ts && <span style={S.ts}>Updated {ts}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <span style={S.badge(session.color)}>{session.open && <span style={S.dot}/>}{session.label}</span>
          <button style={S.refreshBtn} onClick={() => { loadIndices(); loadNifty100(); }}>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={S.loading}>Loading indices…</div>
      ) : (
        <>
          {/* Broad Indices */}
          <div style={S.section}>
            <div style={S.secLabel}>BROAD MARKET</div>
            <div style={S.indexGrid}>
              {broadIdx.map(idx => (
                <div key={idx.id} style={S.indexCard(idx.color, idx.changePct >= 0)}>
                  <div style={S.idxName}>{idx.name}</div>
                  <div style={S.idxPrice}>
                    {idx.price != null ? idx.price.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—'}
                  </div>
                  <div style={S.idxChg(idx.changePct)}>
                    {idx.changePct != null
                      ? `${changeSign(idx.changePct)}${idx.changePct.toFixed(2)}%  ${changeSign(idx.change)}${idx.change?.toFixed(1) ?? ''}`
                      : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sector Indices */}
          <div style={S.section}>
            <div style={S.secLabel}>SECTOR INDICES (NSE)</div>
            <div style={S.indexGrid}>
              {sectorIdx.map(idx => (
                <div key={idx.id} style={S.indexCard(idx.color, idx.changePct >= 0)}>
                  <div style={S.idxName}>{idx.name}</div>
                  <div style={S.idxPrice}>
                    {idx.price != null ? idx.price.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—'}
                  </div>
                  <div style={S.idxChg(idx.changePct)}>
                    {idx.changePct != null
                      ? `${changeSign(idx.changePct)}${idx.changePct.toFixed(2)}%`
                      : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Gainers & Losers */}
          {!n100Loading && (
            <div style={S.section}>
              <div style={S.secLabel}>TOP MOVERS — NIFTY 100</div>
              <div style={S.twoCol}>
                {/* Gainers */}
                <div style={S.movers}>
                  <div style={S.mTitle('#10b981')}>TOP GAINERS</div>
                  {gainers.map(s => (
                    <div key={s.symbol} style={S.row}>
                      <div>
                        <div style={S.rowName}>{s.shortName || s.name}</div>
                        <div style={S.rowSub}>{s.sector} · {s.niftyIndex === 'NIFTY50' ? 'N50' : 'N100'}</div>
                      </div>
                      <div style={S.rowRight}>
                        <div style={S.rowPrice}>{formatPrice(s.price)}</div>
                        <div style={S.rowChg(s.changePct)}>
                          {changeSign(s.changePct)}{s.changePct?.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Losers */}
                <div style={S.movers}>
                  <div style={S.mTitle('#ef4444')}>TOP LOSERS</div>
                  {losers.map(s => (
                    <div key={s.symbol} style={S.row}>
                      <div>
                        <div style={S.rowName}>{s.shortName || s.name}</div>
                        <div style={S.rowSub}>{s.sector} · {s.niftyIndex === 'NIFTY50' ? 'N50' : 'N100'}</div>
                      </div>
                      <div style={S.rowRight}>
                        <div style={S.rowPrice}>{formatPrice(s.price)}</div>
                        <div style={S.rowChg(s.changePct)}>
                          {changeSign(s.changePct)}{s.changePct?.toFixed(2)}%
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Sector Performance from Nifty 100 data */}
          {!n100Loading && sectors.length > 0 && (
            <div style={S.section}>
              <div style={S.secLabel}>SECTOR PERFORMANCE (avg % from Nifty 100)</div>
              <div style={S.sectorGrid}>
                {sectors.map(sec => (
                  <div key={sec.name} style={S.secCard(sec.avg)}>
                    <div style={S.secName}>{sec.name}</div>
                    <div style={S.secPct(sec.avg)}>
                      {changeSign(sec.avg)}{sec.avg.toFixed(2)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {n100Loading && (
            <div style={S.loading}>Loading Nifty 100 data (first load ~15s)…</div>
          )}
        </>
      )}
    </div>
  );
}
